import { useCallback, useMemo, useState } from 'react'
import { arriveHousingPayment, initialUnits } from '../data/loan'
import { currentUser, savedScenarios } from '../data/scenarios'
import { DscrScenario, HousingPayment, HousingPaymentKey, RentalUnit } from '../types/dscr'
import { calculateDscr } from '../utils/dscr'

export function useDscrCalculator() {
  const [units, setUnits] = useState<RentalUnit[]>(initialUnits)
  const [housingPayment, setHousingPayment] = useState<HousingPayment>(arriveHousingPayment)
  const [scenarios, setScenarios] = useState<DscrScenario[]>(savedScenarios)
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>('scenario-1')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState('2026-09-14T09:12:00-06:00')

  const dscr = useMemo(() => calculateDscr(units, housingPayment), [units, housingPayment])

  const activeScenario = useMemo(
    () => scenarios.find((scenario) => scenario.id === activeScenarioId) ?? null,
    [scenarios, activeScenarioId],
  )

  const isDirty = useMemo(() => {
    if (!activeScenario) return true
    const sameRent = activeScenario.units.every(
      (unit, index) =>
        units[index] &&
        units[index].monthlyRent === unit.monthlyRent &&
        units[index].label === unit.label,
    )
    const samePayment = (Object.keys(housingPayment) as HousingPaymentKey[]).every(
      (key) => housingPayment[key] === activeScenario.housingPayment[key],
    )
    return !(sameRent && samePayment && activeScenario.units.length === units.length)
  }, [activeScenario, units, housingPayment])

  const housingPaymentOverridden = useMemo(
    () =>
      (Object.keys(housingPayment) as HousingPaymentKey[]).some(
        (key) => housingPayment[key] !== arriveHousingPayment[key],
      ),
    [housingPayment],
  )

  const setUnitRent = useCallback((id: string, monthlyRent: number) => {
    setUnits((previous) =>
      previous.map((unit) => (unit.id === id ? { ...unit, monthlyRent } : unit)),
    )
  }, [])

  const setUnitLabel = useCallback((id: string, label: string) => {
    setUnits((previous) => previous.map((unit) => (unit.id === id ? { ...unit, label } : unit)))
  }, [])

  const addUnit = useCallback(() => {
    setUnits((previous) => [
      ...previous,
      {
        id: `unit-${previous.length + 1}-${Date.now()}`,
        label: `Unit ${previous.length + 1}`,
        monthlyRent: 0,
      },
    ])
  }, [])

  const removeUnit = useCallback((id: string) => {
    setUnits((previous) =>
      previous.length > 1 ? previous.filter((unit) => unit.id !== id) : previous,
    )
  }, [])

  const setPaymentField = useCallback((key: HousingPaymentKey, value: number) => {
    setHousingPayment((previous) => ({ ...previous, [key]: value }))
  }, [])

  const refreshFromArrive = useCallback(() => {
    setIsRefreshing(true)
    window.setTimeout(() => {
      setHousingPayment(arriveHousingPayment)
      setLastSyncedAt(new Date().toISOString())
      setIsRefreshing(false)
    }, 900)
  }, [])

  const saveScenario = useCallback(
    (name: string, note: string) => {
      const scenario: DscrScenario = {
        id: `scenario-${Date.now()}`,
        name: name.trim() || `Scenario ${scenarios.length + 1}`,
        units: units.map((unit) => ({ ...unit })),
        housingPayment: { ...housingPayment },
        dscr: calculateDscr(units, housingPayment),
        savedBy: currentUser,
        savedAt: new Date().toISOString(),
        version: 1,
        note: note.trim() || undefined,
      }
      setScenarios((previous) => [scenario, ...previous])
      setActiveScenarioId(scenario.id)
      return scenario
    },
    [housingPayment, scenarios.length, units],
  )

  const overwriteScenario = useCallback(
    (id: string, note: string) => {
      const updated: DscrScenario[] = scenarios.map((scenario) =>
        scenario.id === id
          ? {
              ...scenario,
              units: units.map((unit) => ({ ...unit })),
              housingPayment: { ...housingPayment },
              dscr: calculateDscr(units, housingPayment),
              savedBy: currentUser,
              savedAt: new Date().toISOString(),
              version: scenario.version + 1,
              note: note.trim() || scenario.note,
            }
          : scenario,
      )
      setScenarios(updated)
      setActiveScenarioId(id)
      return updated.find((scenario) => scenario.id === id) ?? null
    },
    [housingPayment, scenarios, units],
  )

  const loadScenario = useCallback((scenario: DscrScenario) => {
    setUnits(scenario.units.map((unit) => ({ ...unit })))
    setHousingPayment({ ...scenario.housingPayment })
    setActiveScenarioId(scenario.id)
  }, [])

  return {
    units,
    housingPayment,
    scenarios,
    activeScenario,
    activeScenarioId,
    dscr,
    isDirty,
    isRefreshing,
    lastSyncedAt,
    housingPaymentOverridden,
    setUnitRent,
    setUnitLabel,
    addUnit,
    removeUnit,
    setPaymentField,
    refreshFromArrive,
    saveScenario,
    overwriteScenario,
    loadScenario,
  }
}
